/**
 * Minimal axios client for /api/admin/* — handles JWT and 401 redirect.
 * Token is stored in localStorage AND set as httpOnly cookie by the
 * backend, so refreshing the page keeps you logged in either way.
 */
import axios from "axios";
import { apiOrigin } from "./apiBase";

const TOKEN_KEY = "cleany_admin_token";
export const ADMIN_LOGIN_PATH = "/admin/login";

const baseURL = `${apiOrigin}/api/admin`;

const client = axios.create({
    baseURL,
    withCredentials: true,
});

client.interceptors.request.use((config) => {
    const t = getToken();
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

client.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401 && typeof window !== "undefined") {
            clearToken();
            if (!window.location.pathname.startsWith(ADMIN_LOGIN_PATH)) {
                window.location.replace(ADMIN_LOGIN_PATH);
            }
        }
        return Promise.reject(err);
    },
);

export function getToken() {
    try {
        return window.localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
}

export function setToken(t) {
    try {
        window.localStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
        console.warn("adminApi: setToken failed", e);
    }
}

export function clearToken() {
    try {
        window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
        console.warn("adminApi: clearToken failed", e);
    }
}

export function formatApiError(err) {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || "Ukendt fejl";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
        return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    }
    if (d.msg) return d.msg;
    return JSON.stringify(d);
}

/* ===== Auth ===== */
export async function adminLogin(email, password) {
    const { data } = await client.post("/auth/login", { email, password });
    setToken(data.token);
    return data;
}
export async function adminLogout() {
    try {
        await client.post("/auth/logout");
    } catch (e) {
        console.warn("adminApi: server logout failed (clearing local token anyway)", e);
    }
    clearToken();
}
export async function adminMe() {
    const { data } = await client.get("/auth/me");
    return data;
}

/* ===== Data ===== */
export async function adminStats() {
    const { data } = await client.get("/stats");
    return data;
}
export async function adminWaitlist() {
    const { data } = await client.get("/waitlist");
    return data;
}
export async function adminContacts() {
    const { data } = await client.get("/contacts");
    return data;
}
export async function adminDeleteWaitlist(id) {
    const { data } = await client.delete(`/waitlist/${id}`);
    return data;
}
export async function adminDeleteContact(id) {
    const { data } = await client.delete(`/contacts/${id}`);
    return data;
}
export const adminCatalogPdfUrl = `${apiOrigin}/api/admin/catalog/modulkatalog.pdf`;

/* ===== Legal docs ===== */
export async function adminGetLegal(slug) {
    const { data } = await client.get(`/legal/${slug}`);
    return data;
}
export async function adminUpdateLegal(slug, payload) {
    const { data } = await client.put(`/legal/${slug}`, payload);
    return data;
}

/* ===== Analytics ===== */
export async function adminAnalyticsSummary(days = 30) {
    const { data } = await client.get(`/analytics/summary?days=${days}`);
    return data;
}
export async function adminListPosts() {
    const { data } = await client.get("/posts");
    return data;
}
export async function adminGetPost(slug) {
    const { data } = await client.get(`/posts/${slug}`);
    return data;
}
export async function adminCreatePost(payload) {
    const { data } = await client.post("/posts", payload);
    return data;
}
export async function adminUpdatePost(slug, payload) {
    const { data } = await client.put(`/posts/${slug}`, payload);
    return data;
}
export async function adminDeletePost(slug) {
    const { data } = await client.delete(`/posts/${slug}`);
    return data;
}

/* ===== Users (sales rep management — super_admin only) ===== */
export async function adminListUsers() {
    const { data } = await client.get("/users");
    return data;
}
export async function adminCreateUser(payload) {
    const { data } = await client.post("/users", payload);
    return data;
}
export async function adminUpdateUser(id, payload) {
    const { data } = await client.put(`/users/${id}`, payload);
    return data;
}
export async function adminDeleteUser(id) {
    const { data } = await client.delete(`/users/${id}`);
    return data;
}
export async function adminTestDailyDigest() {
    const { data } = await client.post("/crm/test-daily-digest");
    return data;
}
